import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useCancelTimeOffRequest } from '../../hooks/useCancelTimeOffRequest';

interface CancelRequestActionProps {
  requestId: string;
  disabled?: boolean;
}

/** Requester only — enforced server-side. Only meaningful while the request is still `Pending`. */
export function CancelRequestAction({ requestId, disabled }: CancelRequestActionProps) {
  const { t } = useTranslation('timeOff');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const cancelRequest = useCancelTimeOffRequest();

  async function handleClick() {
    const result = await confirm({
      title: t('requests.cancel.confirmTitle'),
      description: t('requests.cancel.confirmDescription'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    cancelRequest.mutate(requestId);
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleClick}
            disabled={disabled || cancelRequest.isPending}
            aria-label={t('requests.cancel.confirmTitle')}
            data-testid="time-off-request-cancel-btn"
          >
            <Trash2 className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('requests.cancel.confirmTitle')}</TooltipContent>
      </Tooltip>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
